import React, { useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon, SubdirectoryArrowRight as SubtaskIcon } from '@mui/icons-material';
import { PRIORITIES } from './BoardTaskDialog';
import { isOverdue } from './KanbanBoard';

const PRIORITY_RANK = { high: 3, medium: 2, low: 1 };
const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');

/**
 * The board's tasks as a table: one row per task (subtasks too, marked with their parent), one column per
 * attribute. Columns sort on a click; a row opens the task; the status and done columns change it in place.
 * The same filters as the other views (visibleTaskIds).
 */
const TaskTable = ({ lists, tasks, visibleTaskIds, canManage, onOpenTask, onToggleDone, onMoveToList, onDeleteTask }) => {
  const [sort, setSort] = useState({ by: 'status', dir: 'asc' });
  const [statusMenu, setStatusMenu] = useState(null); // { anchor, task }

  const listById = useMemo(() => Object.fromEntries(lists.map((l) => [l.id, l])), [lists]);
  const taskById = useMemo(() => Object.fromEntries(tasks.map((t) => [t.id, t])), [tasks]);
  const subtaskCount = useMemo(() => tasks.reduce((n, t) => (t.parent ? { ...n, [t.parent]: (n[t.parent] || 0) + 1 } : n), {}), [tasks]);

  // What each column sorts by (empty values last, whichever the direction)
  const COLUMNS = [
    { key: 'done', label: 'Done', value: (t) => (t.is_done ? 1 : 0), width: 56 },
    { key: 'title', label: 'Task', value: (t) => t.title.toLowerCase() },
    { key: 'status', label: 'Status', value: (t) => listById[t.board_list]?.position ?? 0 },
    { key: 'priority', label: 'Priority', value: (t) => PRIORITY_RANK[t.priority] || 0 },
    { key: 'start_date', label: 'Start', value: (t) => t.start_date || null },
    { key: 'due_date', label: 'Due', value: (t) => t.due_date || null },
    { key: 'assignees', label: 'Assignees', value: (t) => (t.assignee_names || []).map((a) => a.name).join(', ').toLowerCase() || null },
    { key: 'section', label: 'Linked section', value: (t) => (t.linked_section_title || '').toLowerCase() || null },
    { key: 'description', label: 'Description', value: (t) => (t.description || '').toLowerCase() || null },
    { key: 'updated', label: 'Updated', value: (t) => t.updated || null },
  ];

  const rows = useMemo(() => {
    const column = COLUMNS.find((c) => c.key === sort.by) || COLUMNS[2];
    const sign = sort.dir === 'asc' ? 1 : -1;
    return tasks
      .filter((t) => !visibleTaskIds || visibleTaskIds.has(t.id))
      .sort((a, b) => {
        const va = column.value(a);
        const vb = column.value(b);
        if (va === null && vb === null) return a.position - b.position;
        if (va === null) return 1;
        if (vb === null) return -1;
        return (va < vb ? -1 : va > vb ? 1 : 0) * sign || a.position - b.position;
      });
  }, [tasks, visibleTaskIds, sort, listById]); // eslint-disable-line react-hooks/exhaustive-deps

  const sortBy = (key) => setSort((s) => ({ by: key, dir: s.by === key && s.dir === 'asc' ? 'desc' : 'asc' }));
  const stop = (fn) => (e) => { e.stopPropagation(); fn(e); };

  if (rows.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
        <Typography color="text.secondary">No tasks to show.</Typography>
      </Paper>
    );
  }

  return (
    <>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '70vh' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {COLUMNS.map((c) => (
                <TableCell key={c.key} sx={{ fontWeight: 700, whiteSpace: 'nowrap', width: c.width }}
                  sortDirection={sort.by === c.key ? sort.dir : false}>
                  <TableSortLabel active={sort.by === c.key} direction={sort.by === c.key ? sort.dir : 'asc'} onClick={() => sortBy(c.key)}>
                    {c.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              {canManage && <TableCell sx={{ width: 48 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((t) => {
              const list = listById[t.board_list];
              const priority = PRIORITIES.find((p) => p.value === t.priority);
              const parent = t.parent ? taskById[t.parent] : null;
              const overdue = isOverdue(t);
              return (
                <TableRow key={t.id} hover onClick={() => onOpenTask(t)} sx={{ cursor: 'pointer', opacity: t.is_done ? 0.6 : 1 }}>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox size="small" checked={Boolean(t.is_done)} onChange={() => onToggleDone(t)}
                      inputProps={{ 'aria-label': t.is_done ? 'Mark as not done' : 'Mark as done' }} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 220 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ textDecoration: t.is_done ? 'line-through' : 'none' }}>
                      {t.title}
                    </Typography>
                    {parent && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <SubtaskIcon sx={{ fontSize: 13 }} /> {parent.title}
                      </Typography>
                    )}
                    {subtaskCount[t.id] > 0 && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {subtaskCount[t.id]} subtask{subtaskCount[t.id] === 1 ? '' : 's'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={list?.name || '—'} variant="outlined"
                      color={list?.is_done_list ? 'success' : 'default'}
                      onClick={stop((e) => setStatusMenu({ anchor: e.currentTarget, task: t }))} />
                  </TableCell>
                  <TableCell>
                    {priority && <Chip size="small" label={priority.label} color={priority.color} variant="outlined" />}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(t.start_date)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', color: overdue ? 'error.main' : 'inherit', fontWeight: overdue ? 600 : 400 }}>
                    {fmtDate(t.due_date)}{overdue && ' · overdue'}
                  </TableCell>
                  <TableCell sx={{ minWidth: 140 }}>
                    {(t.assignee_names || []).map((a) => a.name).join(', ')}
                  </TableCell>
                  <TableCell sx={{ minWidth: 140 }}>{t.linked_section_title || ''}</TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    {t.description && (
                      <Tooltip title={<Box sx={{ whiteSpace: 'pre-wrap' }}>{t.description}</Box>}>
                        <Typography variant="body2" color="text.secondary" noWrap>{t.description}</Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                    {t.updated ? new Date(t.updated).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : ''}
                  </TableCell>
                  {canManage && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Delete task">
                        <IconButton size="small" color="error" onClick={() => onDeleteTask(t)} aria-label="Delete task">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* move a task to another list from its status chip */}
      <Menu anchorEl={statusMenu?.anchor} open={Boolean(statusMenu)} onClose={() => setStatusMenu(null)} sx={{ zIndex: 10001 }}>
        {lists.map((l) => (
          <MenuItem key={l.id} selected={statusMenu?.task.board_list === l.id}
            onClick={() => { onMoveToList(statusMenu.task, l.id); setStatusMenu(null); }}>
            {l.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default TaskTable;
